import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Shield, 
  ShieldCheck, 
  ShieldAlert, 
  Sparkles,
  Brain,
  Eye
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';

interface TrustIndicatorProps {
  isAIGenerated?: boolean;
  isHumanReviewed?: boolean;
  isApproved?: boolean;
  confidenceScore?: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

export const TrustIndicator = ({
  isAIGenerated = false,
  isHumanReviewed = false,
  isApproved = false,
  confidenceScore,
  size = 'md',
  showLabel = true,
  className,
}: TrustIndicatorProps) => {
  const { tx, t } = useLocalization();
  const getTrustLevel = () => {
    if (isApproved) return 'approved';
    if (isHumanReviewed) return 'reviewed';
    if (isAIGenerated) return 'ai-generated';
    return 'unknown';
  };

  const trustLevel = getTrustLevel();

  const config = {
    'approved': {
      icon: ShieldCheck,
      label: 'Approved',
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/30',
      tooltip: 'This content has been reviewed and approved by a human',
    },
    'reviewed': {
      icon: Eye,
      label: 'Reviewed',
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      tooltip: 'This content has been reviewed by a human',
    },
    'ai-generated': {
      icon: Sparkles,
      label: 'AI Generated',
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/30',
      tooltip: 'This content was generated by AI and may need review',
    },
    'unknown': {
      icon: Shield,
      label: 'Unknown',
      color: 'text-muted-foreground',
      bgColor: 'bg-muted',
      borderColor: 'border-muted',
      tooltip: 'Trust status unknown',
    },
  };

  const { icon: Icon, label, color, bgColor, borderColor, tooltip } = config[trustLevel];

  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  const badgeSizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-xs px-2 py-0.5',
    lg: 'text-sm px-2.5 py-1',
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={cn(
              'gap-1.5 cursor-help border',
              color,
              bgColor,
              borderColor,
              badgeSizeClasses[size],
              className
            )}
          >
            <Icon className={sizeClasses[size]} />
            {showLabel && tx(label)}
            {confidenceScore !== undefined && (
              <span className="opacity-70">
                ({Math.round(confidenceScore * 100)}%)
              </span>
            )}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tx(tooltip)}</p>
          {confidenceScore !== undefined && (
            <p className="text-xs text-muted-foreground mt-1">
              
              {t('AI Confidence:')} {Math.round(confidenceScore * 100)}%
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default TrustIndicator;
